import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid2";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import Stack from "@mui/material/Stack";
import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { SearchResponseModel } from "@attelas/shared";
import { PageContext } from "../../../context/PageContext";
import { useDebouncedValue } from "../../../hooks/useDebounceValue";
import { useSearchPayables } from "../../../hooks/useSearchPayables";
import SearchPayableList from "./SearchPayableList";
import { useTaskpaneTelemetry } from "../../../telemetry/useTaskpaneTelemetry";

const SearchPayables = () => {
  const { trackPayableSearchQuerySubmitted, trackPayableSearchResultSelected } = useTaskpaneTelemetry();
  const lastQueryTracked = React.useRef("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [inputValue, setInputValue] = React.useState(() => searchParams.get("input") ?? "");
  const [debouncedInputValue, debouncedSetInputValue] = useDebouncedValue(inputValue, 400);
  const { result, isLoading } = useSearchPayables(debouncedInputValue);
  const [value, setValue] = React.useState<SearchResponseModel | null | undefined>(() => {
    const valueId = searchParams.get("value");
    if (!valueId) return null;
    return result?.find(({ id }) => valueId === id);
  });
  const { setNewTitle } = React.useContext(PageContext);

  React.useEffect(() => {
    setNewTitle("Search Payables");
  }, [setNewTitle]);

  React.useEffect(() => {
    const q = debouncedInputValue.trim();
    if (!q) {
      lastQueryTracked.current = "";
      return;
    }
    if (isLoading) return;
    if (lastQueryTracked.current === q) return;
    lastQueryTracked.current = q;
    trackPayableSearchQuerySubmitted({
      query_length: String(q.length),
      result_count: String(result?.length ?? 0),
    });
  }, [debouncedInputValue, isLoading, result, trackPayableSearchQuerySubmitted]);

  return (
    <>
      <Stack spacing={2} pb={16} minHeight="100%">
        <Grid size={12} position="sticky" top={0} sx={{ backgroundColor: "white", zIndex: 1 }}>
          <Autocomplete
            id="search"
            fullWidth={true}
            loading={isLoading}
            getOptionLabel={(option) => option.name}
            getOptionKey={(option) => option.id}
            options={result ?? []}
            value={value}
            onChange={(_, newValue) => {
              setValue(newValue);
              setSearchParams(newValue ? { input: newValue.name, value: newValue.id } : undefined);
              if (newValue) {
                trackPayableSearchResultSelected({ result_type: newValue.type });
              }
            }}
            inputValue={inputValue}
            onInputChange={(_, newInputValue) => {
              setInputValue(newInputValue);
              debouncedSetInputValue(newInputValue);
            }}
            noOptionsText={inputValue.length === 0 ? "No result, start typing to search" : "No results"}
            renderInput={(params) => (
              <TextField {...params} label="Search customer or invoice" fullWidth variant="filled" autoFocus={true} />
            )}
            renderOption={(props, option) => {
              const { key: _key, ...optionProps } = props;
              const matches = match(option.name, debouncedInputValue, { insideWords: true, findAllOccurrences: true });

              const parts = parse(option.name, matches);

              return (
                <li key={option.id} {...optionProps}>
                  <Grid container sx={{ alignItems: "center", width: "100%" }}>
                    <Grid sx={{ width: "calc(100% - 44px)", wordWrap: "break-word" }}>
                      {parts.map((part, index) => (
                        <Box key={index} component="span" sx={{ fontWeight: part.highlight ? "bold" : "regular" }}>
                          {part.text}
                        </Box>
                      ))}
                    </Grid>
                  </Grid>
                </li>
              );
            }}
          />
        </Grid>
        {value && <SearchPayableList key={value.id} value={value} />}
      </Stack>
    </>
  );
};

export default SearchPayables;
